import React, { useCallback, useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';

/**
 * Play on TV.
 *  - TvSendReceiver runs on a TV: it keeps one waiting request open to the server and starts whatever a phone sends.
 *    If the TV is already playing something it asks first; an idle TV just starts.
 *  - TvSendPicker runs on a phone / computer: lists the TVs that have Zflix open and hands one of them a title.
 */

const DEVICE_KEY = 'zflix_tvsend_device_id';

export function tvSendDeviceId() {
  try {
    let id = window.localStorage.getItem(DEVICE_KEY);
    if (!id || id.length < 8) {
      const a = new Uint8Array(12);
      (window.crypto || window.msCrypto).getRandomValues(a);
      id = Array.from(a, (b) => b.toString(16).padStart(2, '0')).join('');
      window.localStorage.setItem(DEVICE_KEY, id);
    }
    return id;
  } catch (_) {
    if (!window.__zflixTvSendId) window.__zflixTvSendId = `tmp${Math.random().toString(16).slice(2)}${Date.now().toString(16)}`;
    return window.__zflixTvSendId;
  }
}

export function tvSendEligible(item) {
  return !!(
    item &&
    item.source === 'jellyfin' &&
    !item.liveStreamId &&
    (item.type === 'movie' || item.type === 'episode') &&
    item.id != null
  );
}

const post = (url, body, signal) =>
  fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify(body),
    signal,
  });

const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

/**
 * A TV app is rarely closed, so it would keep running an old copy of the site for weeks. Every 10 minutes, if a
 * newer build is published and nothing is playing, reload to pick it up.
 */
export function useTvAutoUpdate(enabled, busy) {
  const busyRef = useRef(busy);
  busyRef.current = busy;
  useEffect(() => {
    if (!enabled) return undefined;
    const check = async () => {
      try {
        if (busyRef.current) return;
        const mine = Array.from(document.scripts).map((s) => s.getAttribute('src') || '').find((s) => /\/static\/js\/main\.[^/]+\.js/.test(s));
        if (!mine) return;
        const r = await fetch(`/asset-manifest.json?_=${Date.now()}`, { cache: 'no-store' });
        if (!r.ok) return;
        const j = await r.json();
        const latest = j && j.files && j.files['main.js'];
        if (typeof latest !== 'string' || !/main\.[^/]+\.js$/.test(latest)) return;
        const name = (s) => s.split('/').pop().split('?')[0];
        if (name(latest) !== name(mine) && !busyRef.current) {
          /** The person may just be sitting on a page: no intro, and they stay where they are. */
          try {
            sessionStorage.setItem('zflix_profile_switch', '1');
            sessionStorage.setItem('zflix_return_section', window.__zflixSection || '');
          } catch (_) {}
          window.location.reload();
        }
      } catch (_) {}
    };
    const first = setTimeout(check, 60 * 1000);
    const iv = setInterval(check, 10 * 60 * 1000);
    return () => {
      clearTimeout(first);
      clearInterval(iv);
    };
  }, [enabled]);
}

/**
 * The phone remote's keys, delivered the way the TV app delivers the real remote's: arrows, Play/Pause, rewind and
 * fast-forward as keydowns on window; OK as a keydown when the player or a question owns the keys, otherwise as a click
 * on whatever has focus; Back through the shared Back handler.
 */
const TV_KEYS = {
  up: ['ArrowUp', 38],
  down: ['ArrowDown', 40],
  left: ['ArrowLeft', 37],
  right: ['ArrowRight', 39],
  playpause: ['MediaPlayPause', 179],
  rew: ['MediaRewind', 227],
  ff: ['MediaFastForward', 228],
  ok: ['Enter', 13],
};
function sendKeydown(key, keyCode) {
  const target = document.activeElement && document.activeElement !== document.body ? document.activeElement : document.body;
  const ev = new KeyboardEvent('keydown', { key, code: key, keyCode, which: keyCode, bubbles: true, cancelable: true });
  /** Older WebViews ignore keyCode in the constructor. */
  try {
    if (ev.keyCode !== keyCode) {
      Object.defineProperty(ev, 'keyCode', { get: () => keyCode });
      Object.defineProperty(ev, 'which', { get: () => keyCode });
    }
  } catch (_) {}
  target.dispatchEvent(ev);
  target.dispatchEvent(new KeyboardEvent('keyup', { key, code: key, bubbles: true, cancelable: true }));
}
export function pressTvKey(name) {
  try {
    if (name === 'back') {
      if (typeof window.__zflixDispatchTvBack === 'function') window.__zflixDispatchTvBack();
      else sendKeydown('Escape', 27);
      return;
    }
    const k = TV_KEYS[name];
    if (!k) return;
    if (name === 'ok') {
      const playerOpen = !!document.querySelector('[data-zflix-player]:not([data-zflix-jellyfin-embed])');
      const el = document.activeElement;
      if (playerOpen || typeof window.__zflixTopKeys === 'function' || !el || el === document.body) sendKeydown(k[0], k[1]);
      else if (typeof el.click === 'function') el.click();
      return;
    }
    sendKeydown(k[0], k[1]);
  } catch (_) {}
}

export function TvSendReceiver({ enabled, nowPlayingTitle, onPlay, onStop, onSearch }) {
  const onStopRef = useRef(onStop);
  onStopRef.current = onStop;
  const onSearchRef = useRef(onSearch);
  onSearchRef.current = onSearch;
  const [ask, setAsk] = useState(null);
  const [pick, setPick] = useState(0);
  const titleRef = useRef(nowPlayingTitle || null);
  const onPlayRef = useRef(onPlay);
  onPlayRef.current = onPlay;
  const acRef = useRef(null);
  const restateRef = useRef(false);

  /** Tell the server straight away when this TV starts / stops playing something. */
  useEffect(() => {
    titleRef.current = nowPlayingTitle || null;
    if (acRef.current) {
      restateRef.current = true;
      try {
        acRef.current.abort();
      } catch (_) {}
    }
  }, [nowPlayingTitle]);

  const report = useCallback((commandId, status) => {
    post('/api/tvsend/result', { commandId, status }).catch(() => {});
  }, []);

  const start = useCallback(
    (cmd) => {
      report(cmd.id, 'playing');
      try {
        onPlayRef.current(cmd.item, cmd);
      } catch (_) {}
    },
    [report],
  );

  useEffect(() => {
    if (!enabled) return undefined;
    let dead = false;
    const deviceId = tvSendDeviceId();
    (async () => {
      while (!dead) {
        try {
          const ac = new AbortController();
          acRef.current = ac;
          const r = await post('/api/tvsend/wait', { deviceId, nowPlaying: titleRef.current ? { title: titleRef.current } : null }, ac.signal);
          acRef.current = null;
          if (!r.ok) {
            await sleep(r.status === 401 ? 20000 : 6000);
            continue;
          }
          const j = await r.json();
          for (const cmd of j.commands || []) {
            if (cmd.type === 'control') {
              /** The phone that started this title is acting as its remote. */
              const v = document.querySelector('video');
              try {
                if (cmd.action === 'stop') onStopRef.current?.();
                else if (!v) continue;
                else if (cmd.action === 'pause') v.pause();
                else if (cmd.action === 'play') v.play().catch(() => {});
                else if (cmd.action === 'back30') v.currentTime = Math.max(0, v.currentTime - 30);
                else if (cmd.action === 'fwd30') v.currentTime = Math.min((v.duration || v.currentTime + 30) - 1, v.currentTime + 30);
              } catch (_) {}
              continue;
            }
            /** A key from a phone being used as this TV's remote: handled as if pressed on the real remote. */
            if (cmd.type === 'key') {
              pressTvKey(cmd.key);
              continue;
            }
            /** A search typed (or spoken) on a phone: shown here unless something is playing. */
            if (cmd.type === 'search') {
              if (!titleRef.current && cmd.query) onSearchRef.current?.(String(cmd.query).slice(0, 80));
              continue;
            }
            if (cmd.type !== 'play' || !cmd.item) continue;
            if (titleRef.current) {
              report(cmd.id, 'asking');
              setPick(0);
              setAsk(cmd);
            } else start(cmd);
          }
        } catch (_) {
          acRef.current = null;
          if (dead) return;
          if (restateRef.current) restateRef.current = false;
          else await sleep(5000);
        }
      }
    })();
    return () => {
      dead = true;
      try {
        acRef.current?.abort();
      } catch (_) {}
    };
  }, [enabled, report, start]);

  const answer = useCallback(
    (yes) => {
      setAsk((cur) => {
        if (cur) {
          if (yes) start(cur);
          else report(cur.id, 'declined');
        }
        return null;
      });
    },
    [report, start],
  );

  /** While the question is up it owns the remote: Left / Right choose, OK answers, Back = keep watching. */
  const pickRef = useRef(0);
  pickRef.current = pick;
  const keepRef = useRef(null);
  const playRef = useRef(null);
  useEffect(() => {
    if (!ask) return undefined;
    /**
     * The TV app delivers arrows as key events on window and OK as a click on whatever has focus, so the chosen
     * button holds real focus. tvSpatialNav hands every key to __zflixTopKeys first (before the player sees it).
     */
    const before = document.activeElement;
    window.__zflixTopKeys = (e) => {
      const k = e.key;
      const code = e.keyCode;
      if (k === 'ArrowLeft' || code === 37 || k === 'ArrowRight' || code === 39) setPick((p) => (p ? 0 : 1));
      else if (k === 'Enter' || code === 13) answer(pickRef.current === 1);
      else if (k === 'Escape' || k === 'GoBack' || k === 'BrowserBack') answer(false);
      return true;
    };
    const prevBack = window.__zflixOwnBack;
    window.__zflixOwnBack = () => {
      answer(false);
      return true;
    };
    const t = setTimeout(() => answer(false), 30000);
    return () => {
      delete window.__zflixTopKeys;
      if (prevBack) window.__zflixOwnBack = prevBack;
      else delete window.__zflixOwnBack;
      clearTimeout(t);
      try {
        if (before && document.contains(before) && typeof before.focus === 'function') before.focus({ preventScroll: true });
      } catch (_) {}
    };
  }, [ask, answer]);
  useEffect(() => {
    if (!ask) return;
    try {
      (pick === 1 ? playRef.current : keepRef.current)?.focus({ preventScroll: true });
    } catch (_) {}
  }, [ask, pick]);

  if (!ask) return null;
  const btn = (on) => ({
    padding: '14px 26px',
    fontSize: 20,
    fontWeight: 700,
    borderRadius: 10,
    border: on ? '3px solid #fff' : '3px solid transparent',
    background: on ? 'var(--accent)' : 'rgba(255,255,255,0.12)',
    color: '#fff',
    cursor: 'pointer',
  });
  return (
    <div style={{ position: 'fixed', left: 0, right: 0, bottom: 0, zIndex: 100000, padding: '28px 40px 36px', background: 'linear-gradient(to top, rgba(0,0,0,0.94) 60%, rgba(0,0,0,0))', display: 'flex', alignItems: 'center', gap: 24, flexWrap: 'wrap' }}>
      <div style={{ flex: '1 1 320px', minWidth: 0 }}>
        <div style={{ fontSize: 15, color: 'var(--text2)', marginBottom: 4 }}>{ask.from} wants to play on this TV</div>
        <div style={{ fontSize: 26, fontWeight: 700, color: '#fff', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{ask.item.title}</div>
      </div>
      <button ref={keepRef} type="button" style={btn(pick === 0)} onClick={(e) => { e.stopPropagation(); answer(false); }}>Keep watching</button>
      <button ref={playRef} type="button" style={btn(pick === 1)} onClick={(e) => { e.stopPropagation(); answer(true); }}>Play it</button>
    </div>
  );
}

/**
 * Search from a phone, shown on a TV: the Fire TV's mic belongs to Alexa, so the phone keyboard's mic does the talking.
 * Lists the TVs with Zflix open; a TV that is playing something keeps playing.
 */
export function TvSearchSend({ query, onClose, onToast }) {
  const [devices, setDevices] = useState(null);
  const [note, setNote] = useState('');
  useEffect(() => {
    let dead = false;
    fetch(`/api/tvsend/devices?self=${encodeURIComponent(tvSendDeviceId())}`, { credentials: 'include' })
      .then((r) => r.json())
      .then((j) => !dead && setDevices(j.devices || []))
      .catch(() => !dead && setDevices([]));
    return () => {
      dead = true;
    };
  }, []);
  const send = async (d) => {
    setNote(`Sending to ${d.name}…`);
    try {
      const r = await post('/api/tvsend/search', { deviceId: d.id, self: tvSendDeviceId(), query });
      const j = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(j.error || 'Could not reach that TV');
      onToast?.(`Showing "${query}" on ${d.name}`, 'success');
      onClose();
    } catch (e) {
      setNote(e.message || 'Could not reach that TV');
    }
  };
  return ReactDOM.createPortal(
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, zIndex: 100001, background: 'rgba(0,0,0,0.72)', display: 'flex', alignItems: 'flex-end', justifyContent: 'center' }}>
      <div onClick={(e) => e.stopPropagation()} style={{ width: '100%', maxWidth: 480, background: '#16161d', borderRadius: '16px 16px 0 0', padding: '18px 16px calc(18px + env(safe-area-inset-bottom, 0px))', border: '1px solid rgba(255,255,255,0.12)', borderBottom: 'none', color: '#fff' }}>
        <div style={{ display: 'flex', alignItems: 'center', marginBottom: 4 }}>
          <div style={{ flex: 1, fontSize: 18, fontWeight: 700 }}>Show on TV</div>
          <button type="button" onClick={onClose} style={{ padding: '6px 12px', borderRadius: 8, border: 'none', background: 'rgba(255,255,255,0.1)', color: '#fff', fontSize: 14, cursor: 'pointer' }}>Close</button>
        </div>
        <div style={{ fontSize: 13, color: 'var(--text2)', marginBottom: 14, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>Search: {query}</div>
        {devices == null ? <div style={{ fontSize: 14, color: 'var(--text2)' }}>Looking for TVs…</div> : null}
        {devices != null && !devices.length ? <div style={{ fontSize: 14, color: 'var(--text2)', lineHeight: 1.5 }}>No TV has Zflix open right now.</div> : null}
        {(devices || []).map((d) => (
          <button key={d.id} type="button" disabled={!!d.nowPlaying} onClick={() => send(d)} style={{ display: 'block', width: '100%', textAlign: 'left', padding: '12px 14px', marginBottom: 8, borderRadius: 10, border: '1px solid rgba(255,255,255,0.16)', background: 'rgba(255,255,255,0.06)', color: d.nowPlaying ? 'var(--text3)' : '#fff', cursor: d.nowPlaying ? 'default' : 'pointer' }}>
            <div style={{ fontSize: 16, fontWeight: 700 }}>{d.name}</div>
            <div style={{ fontSize: 12.5, color: 'var(--text2)', marginTop: 2 }}>{d.nowPlaying ? `Busy: ${d.nowPlaying}` : 'Ready'}</div>
          </button>
        ))}
        {note ? <div style={{ fontSize: 13, color: 'var(--text2)', marginTop: 6 }}>{note}</div> : null}
      </div>
    </div>,
    document.body,
  );
}

/**
 * The phone as a full TV remote (Zach, 2026-11-03): arrows, OK, Back, Play/Pause, rewind / fast-forward and search, for
 * a TV with Zflix open on the same network (or one this phone started something on).
 */
export function TvRemote({ onClose, initialDevice }) {
  const [devices, setDevices] = useState(null);
  const [tv, setTv] = useState(initialDevice || null);
  const [note, setNote] = useState('');
  const [query, setQuery] = useState('');
  const deadRef = useRef(false);

  useEffect(() => {
    deadRef.current = false;
    const load = () =>
      fetch(`/api/tvsend/devices?self=${encodeURIComponent(tvSendDeviceId())}`, { credentials: 'include' })
        .then((r) => r.json())
        .then((j) => {
          if (deadRef.current) return;
          const list = (j.devices || []).filter((d) => d.here || d.mine);
          setDevices(list);
          /** One TV: straight to its remote. */
          setTv((cur) => cur || (list.length === 1 ? { id: list[0].id, name: list[0].name } : null));
        })
        .catch(() => !deadRef.current && setDevices([]));
    load();
    const iv = setInterval(load, 8000);
    return () => {
      deadRef.current = true;
      clearInterval(iv);
    };
  }, []);

  const press = async (key) => {
    if (!tv) return;
    try {
      if (navigator.vibrate) navigator.vibrate(12);
    } catch (_) {}
    try {
      const r = await post('/api/tvsend/key', { deviceId: tv.id, self: tvSendDeviceId(), key });
      if (!r.ok) {
        const j = await r.json().catch(() => ({}));
        throw new Error(j.error || 'The TV did not get that');
      }
      if (note) setNote('');
    } catch (e) {
      setNote(e.message || 'The TV did not get that');
    }
  };
  const search = async (e) => {
    e.preventDefault();
    const q = query.trim();
    if (!tv || !q) return;
    try {
      const r = await post('/api/tvsend/search', { deviceId: tv.id, self: tvSendDeviceId(), query: q });
      const j = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(j.error || 'Could not reach that TV');
      setNote(`Showing "${q}" on the TV`);
      setQuery('');
    } catch (err) {
      setNote(err.message || 'Could not reach that TV');
    }
  };

  const round = { border: '1px solid rgba(255,255,255,0.16)', background: 'rgba(255,255,255,0.08)', color: '#fff', cursor: 'pointer', touchAction: 'manipulation', WebkitTapHighlightColor: 'transparent' };
  const arrow = (key, label, style) => (
    <button type="button" aria-label={key} onClick={() => press(key)} style={{ ...round, position: 'absolute', width: 72, height: 72, borderRadius: 16, fontSize: 26, ...style }}>{label}</button>
  );
  const pill = (key, label) => (
    <button type="button" onClick={() => press(key)} style={{ ...round, flex: 1, padding: '14px 6px', borderRadius: 12, fontSize: 15, fontWeight: 700 }}>{label}</button>
  );

  /** A wide screen (an unfolded Fold, a tablet): the remote sits at the side as a card instead of across the bottom. */
  const wide = typeof window !== 'undefined' && window.innerWidth >= 700;
  return ReactDOM.createPortal(
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, zIndex: 100001, background: wide ? 'rgba(0,0,0,0.45)' : 'rgba(0,0,0,0.72)', display: 'flex', alignItems: wide ? 'center' : 'flex-end', justifyContent: wide ? 'flex-end' : 'center' }}>
      <div onClick={(e) => e.stopPropagation()} style={{ width: '100%', maxWidth: 420, maxHeight: '92vh', overflowY: 'auto', background: '#16161d', borderRadius: wide ? 16 : '16px 16px 0 0', margin: wide ? '0 24px 0 0' : 0, padding: '18px 16px calc(18px + env(safe-area-inset-bottom, 0px))', border: '1px solid rgba(255,255,255,0.12)', borderBottom: wide ? '1px solid rgba(255,255,255,0.12)' : 'none', color: '#fff' }}>
        <div style={{ display: 'flex', alignItems: 'center', marginBottom: 12 }}>
          <div style={{ flex: 1, minWidth: 0, fontSize: 18, fontWeight: 700, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{tv ? `Remote · ${tv.name}` : 'TV remote'}</div>
          <button type="button" onClick={onClose} style={{ padding: '6px 12px', borderRadius: 8, border: 'none', background: 'rgba(255,255,255,0.1)', color: '#fff', fontSize: 14, cursor: 'pointer' }}>Close</button>
        </div>
        {!tv ? (
          <div>
            {devices == null ? <div style={{ fontSize: 14, color: 'var(--text2)' }}>Looking for TVs…</div> : null}
            {devices != null && !devices.length ? (
              <div style={{ fontSize: 14, color: 'var(--text2)', lineHeight: 1.5 }}>No TV with Zflix open on this network. Open Zflix on the TV and it shows up here.</div>
            ) : null}
            {(devices || []).map((d) => (
              <button key={d.id} type="button" onClick={() => setTv({ id: d.id, name: d.name })} style={{ display: 'block', width: '100%', textAlign: 'left', padding: '12px 14px', marginBottom: 8, borderRadius: 10, border: '1px solid rgba(255,255,255,0.16)', background: 'rgba(255,255,255,0.06)', color: '#fff', cursor: 'pointer' }}>
                <div style={{ fontSize: 16, fontWeight: 700 }}>{d.name}</div>
                <div style={{ fontSize: 12.5, color: 'var(--text2)', marginTop: 2 }}>{d.nowPlaying ? `Watching: ${d.nowPlaying}` : 'Nothing playing'}</div>
              </button>
            ))}
          </div>
        ) : (
          <div>
            <div style={{ position: 'relative', width: 232, height: 232, margin: '4px auto 16px' }}>
              {arrow('up', '▲', { left: 80, top: 0 })}
              {arrow('left', '◀', { left: 0, top: 80 })}
              {arrow('right', '▶', { right: 0, top: 80 })}
              {arrow('down', '▼', { left: 80, bottom: 0 })}
              <button type="button" onClick={() => press('ok')} style={{ ...round, position: 'absolute', left: 80, top: 80, width: 72, height: 72, borderRadius: '50%', background: 'var(--accent, #e50914)', border: 'none', fontSize: 18, fontWeight: 800 }}>OK</button>
            </div>
            <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
              {pill('back', '↩ Back')}
              {pill('playpause', '⏯ Play / Pause')}
            </div>
            <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
              {pill('rew', '« Rewind')}
              {pill('ff', 'Forward »')}
            </div>
            <form onSubmit={search} style={{ display: 'flex', gap: 8 }}>
              <input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search on the TV"
                enterKeyHint="search"
                style={{ flex: 1, minWidth: 0, padding: '12px 12px', borderRadius: 10, border: '1px solid rgba(255,255,255,0.16)', background: 'rgba(255,255,255,0.06)', color: '#fff', fontSize: 15 }}
              />
              <button type="submit" style={{ ...round, padding: '0 16px', borderRadius: 10, fontSize: 15, fontWeight: 700 }}>Go</button>
            </form>
            <div style={{ minHeight: 20, fontSize: 13, color: 'var(--text2)', marginTop: 10 }}>{note}</div>
            {devices && devices.length > 1 ? (
              <button type="button" onClick={() => { setTv(null); setNote(''); }} style={{ padding: '8px 2px', border: 'none', background: 'transparent', color: 'var(--text2)', fontSize: 13, cursor: 'pointer' }}>‹ Other TVs</button>
            ) : null}
          </div>
        )}
      </div>
    </div>,
    document.body,
  );
}

export function TvSendPicker({ item, getPositionSec, onClose, onPlaying, onToast }) {
  const [devices, setDevices] = useState(null);
  const [error, setError] = useState('');
  const [showOther, setShowOther] = useState(false);
  /** null | { id, name, state: 'sending' | 'asking' | 'declined' | 'noanswer' } */
  const [sending, setSending] = useState(null);
  /** The TV this phone is acting as a remote for: { id, name }. */
  const [remote, setRemote] = useState(null);
  const [remoteNote, setRemoteNote] = useState('');
  const [full, setFull] = useState(false);
  const deadRef = useRef(false);

  const load = useCallback(async () => {
    try {
      const r = await fetch(`/api/tvsend/devices?self=${encodeURIComponent(tvSendDeviceId())}`, { credentials: 'include' });
      if (!r.ok) throw new Error('Could not look for TVs');
      const j = await r.json();
      if (!deadRef.current) {
        setDevices(j.devices || []);
        setError('');
      }
    } catch (e) {
      if (!deadRef.current) setError(e.message || 'Could not look for TVs');
    }
  }, []);

  useEffect(() => {
    deadRef.current = false;
    load();
    const iv = setInterval(load, 5000);
    return () => {
      deadRef.current = true;
      clearInterval(iv);
    };
  }, [load]);

  const send = async (d) => {
    if (sending && sending.state === 'sending') return;
    setSending({ id: d.id, name: d.name, state: 'sending' });
    try {
      const positionSec = Math.max(0, Math.floor(Number(getPositionSec ? getPositionSec() : 0) || 0));
      const r = await post('/api/tvsend/send', {
        deviceId: d.id,
        self: tvSendDeviceId(),
        positionSec,
        item: {
          id: item.id, type: item.type, title: item.title, name: item.name, year: item.year, overview: item.overview,
          seriesId: item.seriesId, seasonIndex: item.seasonIndex, episodeIndex: item.episodeIndex,
        },
      });
      const j = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(j.error || 'Could not reach that TV');
      for (let i = 0; i < 45 && !deadRef.current; i += 1) {
        await sleep(1000);
        const rr = await fetch(`/api/tvsend/result?id=${encodeURIComponent(j.commandId)}`, { credentials: 'include' });
        const st = (await rr.json().catch(() => ({}))).status;
        if (st === 'playing') {
          onToast?.(`Playing on ${d.name}`, 'success');
          if (!deadRef.current) {
            setSending(null);
            setRemoteNote('');
            setRemote({ id: d.id, name: d.name });
          }
          onPlaying?.(d);
          return;
        }
        if (st === 'declined') {
          if (!deadRef.current) setSending({ id: d.id, name: d.name, state: 'declined' });
          return;
        }
        if (st === 'asking' && !deadRef.current) setSending({ id: d.id, name: d.name, state: 'asking' });
      }
      if (!deadRef.current) setSending({ id: d.id, name: d.name, state: 'noanswer' });
    } catch (e) {
      if (!deadRef.current) {
        setSending(null);
        setError(e.message || 'Could not reach that TV');
      }
    }
  };

  const control = async (action) => {
    if (!remote) return;
    try {
      const r = await post('/api/tvsend/control', { deviceId: remote.id, self: tvSendDeviceId(), action });
      const j = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(j.error || 'The TV did not get that');
      setRemoteNote(action === 'stop' ? 'Stopped.' : action === 'pause' ? 'Paused.' : action === 'play' ? 'Playing.' : action === 'back30' ? 'Back 30 seconds.' : 'Forward 30 seconds.');
      if (action === 'stop') setTimeout(() => { if (!deadRef.current) { setRemote(null); load(); } }, 900);
    } catch (e) {
      setRemoteNote(e.message || 'The TV did not get that');
    }
  };

  const rename = async (d) => {
    const name = window.prompt('Name for this TV', d.name);
    if (!name || !name.trim()) return;
    try {
      await post('/api/tvsend/name', { deviceId: d.id, name: name.trim() });
    } catch (_) {}
    load();
  };

  const here = (devices || []).filter((d) => d.here);
  const other = (devices || []).filter((d) => !d.here);
  const row = (d) => {
    const mine = sending && sending.id === d.id ? sending.state : null;
    return (
      <div key={d.id} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
        <button
          type="button"
          onClick={() => send(d)}
          style={{ flex: 1, minWidth: 0, textAlign: 'left', padding: '12px 14px', borderRadius: 10, border: '1px solid rgba(255,255,255,0.16)', background: mine ? 'rgba(255,255,255,0.14)' : 'rgba(255,255,255,0.06)', color: '#fff', cursor: 'pointer' }}
        >
          <div style={{ fontSize: 16, fontWeight: 700 }}>{d.name}</div>
          <div style={{ fontSize: 12.5, color: 'var(--text2)', marginTop: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {mine === 'sending' ? 'Sending…'
              : mine === 'asking' ? 'Asking on the TV — waiting for an answer…'
              : mine === 'declined' ? 'They chose to keep watching.'
              : mine === 'noanswer' ? 'No answer from the TV.'
              : d.nowPlaying ? `Watching: ${d.nowPlaying}` : 'Nothing playing'}
          </div>
        </button>
        {d.mine && d.nowPlaying ? (
          <button type="button" onClick={() => { setRemoteNote(''); setRemote({ id: d.id, name: d.name }); }} style={{ padding: '10px 12px', borderRadius: 10, border: '1px solid var(--accent)', background: 'transparent', color: '#fff', fontSize: 12.5, cursor: 'pointer' }}>
            Remote
          </button>
        ) : null}
        <button type="button" onClick={() => rename(d)} aria-label={`Rename ${d.name}`} style={{ padding: '10px 12px', borderRadius: 10, border: '1px solid rgba(255,255,255,0.16)', background: 'transparent', color: 'var(--text2)', fontSize: 12.5, cursor: 'pointer' }}>
          Rename
        </button>
      </div>
    );
  };

  if (full && remote) return <TvRemote initialDevice={remote} onClose={() => setFull(false)} />;
  /** Drawn on top of the page (or of the full-screen player), not inside whichever sheet opened it. */
  const host =document.fullscreenElement || document.webkitFullscreenElement || document.body;
  return ReactDOM.createPortal(
    <div
      onClick={(e) => { e.stopPropagation(); onClose(); }}
      style={{ position: 'fixed', inset: 0, zIndex: 100001, background: 'rgba(0,0,0,0.72)', display: 'flex', alignItems: 'flex-end', justifyContent: 'center' }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{ width: '100%', maxWidth: 480, maxHeight: '80vh', overflowY: 'auto', background: '#16161d', borderRadius: '16px 16px 0 0', padding: '18px 16px calc(18px + env(safe-area-inset-bottom, 0px))', border: '1px solid rgba(255,255,255,0.12)', borderBottom: 'none', color: '#fff' }}
      >
        <div style={{ display: 'flex', alignItems: 'center', marginBottom: 4 }}>
          <div style={{ flex: 1, fontSize: 18, fontWeight: 700 }}>Play on TV</div>
          <button type="button" onClick={onClose} style={{ padding: '6px 12px', borderRadius: 8, border: 'none', background: 'rgba(255,255,255,0.1)', color: '#fff', fontSize: 14, cursor: 'pointer' }}>Close</button>
        </div>
        <div style={{ fontSize: 13, color: 'var(--text2)', marginBottom: 14, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{remote ? `Playing on ${remote.name}` : item.title}</div>
        {remote ? (
          <div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
              {[['pause', 'Pause'], ['play', 'Resume'], ['back30', '« 30 sec'], ['fwd30', '30 sec »']].map(([a, label]) => (
                <button key={a} type="button" onClick={() => control(a)} style={{ padding: '16px 10px', borderRadius: 12, border: '1px solid rgba(255,255,255,0.16)', background: 'rgba(255,255,255,0.08)', color: '#fff', fontSize: 16, fontWeight: 700, cursor: 'pointer' }}>{label}</button>
              ))}
            </div>
            <button type="button" onClick={() => control('stop')} style={{ width: '100%', marginTop: 10, padding: '14px 10px', borderRadius: 12, border: '1px solid rgba(255,120,120,0.5)', background: 'rgba(255,80,80,0.14)', color: '#fff', fontSize: 16, fontWeight: 700, cursor: 'pointer' }}>Stop</button>
            <div style={{ minHeight: 20, fontSize: 13, color: 'var(--text2)', marginTop: 10 }}>{remoteNote}</div>
            <button type="button" onClick={() => setFull(true)} style={{ width: '100%', marginBottom: 6, padding: '12px 10px', borderRadius: 12, border: '1px solid rgba(255,255,255,0.16)', background: 'transparent', color: '#fff', fontSize: 15, fontWeight: 700, cursor: 'pointer' }}>Full remote (arrows, OK, Back)</button>
            <button type="button" onClick={() => { setRemote(null); load(); }} style={{ padding: '8px 2px', border: 'none', background: 'transparent', color: 'var(--text2)', fontSize: 13, cursor: 'pointer' }}>‹ All TVs</button>
          </div>
        ) : (
        <>
        {error ? <div style={{ fontSize: 13, color: '#ff8a8a', marginBottom: 10 }}>{error}</div> : null}
        {devices == null && !error ? <div style={{ fontSize: 14, color: 'var(--text2)', padding: '10px 0' }}>Looking for TVs…</div> : null}
        {devices != null && !devices.length ? (
          <div style={{ fontSize: 14, color: 'var(--text2)', padding: '6px 0 10px', lineHeight: 1.5 }}>
            No TV has Zflix open right now. Open Zflix on the TV and it will appear here.
          </div>
        ) : null}
        {here.length ? <div style={{ fontSize: 11.5, letterSpacing: 1, textTransform: 'uppercase', color: 'var(--text2)', margin: '4px 0 8px' }}>On this network</div> : null}
        {here.map(row)}
        {other.length ? (
          <>
            <button type="button" onClick={() => setShowOther((v) => !v)} style={{ width: '100%', textAlign: 'left', padding: '10px 2px', border: 'none', background: 'transparent', color: 'var(--text2)', fontSize: 13, cursor: 'pointer' }}>
              {showOther ? '▾' : '▸'} Other locations ({other.length})
            </button>
            {showOther ? other.map(row) : null}
          </>
        ) : null}
        </>
        )}
      </div>
    </div>,
    host,
  );
}
